/**
 * Diamond Search Bridge — Patches PredictiveSearch to show diamond suggestions,
 * live diamond inventory cards, and hide diamond products from Shopify results.
 *
 * Depends on: diamond-query-parser.js (must load first)
 * Patches: PredictiveSearch web component from theme.js (NOT modified)
 */
(function() {
  'use strict';

  // ─── Supabase config (same as diamond-search page) ───────────
  var SUPABASE_URL = 'https://ofjwrrqzzbcnmkkmlawl.supabase.co';
  var SUPABASE_ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9mandycnF6emJjbm1ra21sYXdsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI0OTg5MjMsImV4cCI6MjA4ODA3NDkyM30.3ylsE56aZXXmvPP4oiUaLd4pBbYPSU1HCfcf2XAqMjM';
  var REST_BASE = SUPABASE_URL + '/rest/v1/public_diamonds';

  document.addEventListener('DOMContentLoaded', function() {
    if (!window.DiamondQueryParser) return;

    var PSClass = customElements.get('predictive-search');
    if (!PSClass) return;

    var originalGetResults = PSClass.prototype.getSearchResults;
    if (!originalGetResults) return;

    // ── Patch getSearchResults ──────────────────────────────────
    PSClass.prototype.getSearchResults = function(searchTerm) {
      var self = this;
      var parsed = window.DiamondQueryParser.parse(searchTerm);

      // Call original Shopify fetch
      var originalPromise = originalGetResults.call(this, searchTerm);

      if (parsed.isDiamondQuery) {
        // Fetch real diamonds from Supabase in parallel
        fetchDiamondPreview(parsed).then(function(diamonds) {
          // Wait a tick for Shopify results to render, then inject
          waitForResults(self, function(container) {
            hideDiamondProducts(container);
            injectDiamondSection(container, parsed, diamonds);
          });
        });
      } else {
        // Still hide diamond products even for non-diamond queries
        waitForResults(self, function(container) {
          hideDiamondProducts(container);
        });
      }

      return originalPromise;
    };

    // ── Wait for Shopify results to render ─────────────────────
    function waitForResults(element, callback) {
      var container = element.querySelector('#predictive-search') ||
                      element.querySelector('.search__results');
      if (!container) return;

      var observer = new MutationObserver(function(mutations, obs) {
        obs.disconnect();
        // Small delay to ensure DOM is fully updated
        setTimeout(function() { callback(container); }, 30);
      });
      observer.observe(container, { childList: true, subtree: true });

      // Fallback timeout
      setTimeout(function() {
        observer.disconnect();
        callback(container);
      }, 800);
    }

    // ── Hide diamond products from Shopify results ─────────────
    function hideDiamondProducts(container) {
      // Diamond products have titles like "0.19ct Round Lab Grown Diamond (Colour D, ..."
      // They match: starts with a carat weight + shape + "Lab Grown Diamond"
      var productLinks = container.querySelectorAll('.results--products a, .results--products li');
      for (var i = 0; i < productLinks.length; i++) {
        var el = productLinks[i];
        var text = (el.textContent || '').trim();
        if (isDiamondProductText(text)) {
          // Hide the list item
          var li = el.closest('li') || el;
          li.style.display = 'none';
        }
      }

      // Also check product result items by looking at the link href
      var allLinks = container.querySelectorAll('a[href*="/products/"]');
      for (var j = 0; j < allLinks.length; j++) {
        var link = allLinks[j];
        var href = link.getAttribute('href') || '';
        var linkText = (link.textContent || '').trim();
        // Diamond product URLs contain "lab-grown-diamond" or similar patterns
        if (isDiamondProductText(linkText) || isDiamondProductURL(href)) {
          var listItem = link.closest('li') || link.closest('[role="option"]') || link;
          listItem.style.display = 'none';
        }
      }
    }

    function isDiamondProductText(text) {
      // Matches: "0.19ct Round Lab Grown Diamond (Colour D, Clarity VS2..."
      return /\d+\.?\d*\s*ct\w?\s+\w+\s+Lab\s+Grown\s+Diamond/i.test(text);
    }

    function isDiamondProductURL(href) {
      // Diamond product handles typically contain carat + shape + lab-grown-diamond
      return /\d+-?\d*ct-?\w+-lab-grown-diamond/i.test(href) ||
             /lab-grown-diamond.*colour/i.test(href);
    }

    // ── Fetch diamond preview from Supabase ────────────────────
    function fetchDiamondPreview(parsed) {
      var params = ['select=sku,shape,carat,color,clarity,cut,lab,price_usd,markup_price,image_url,ratio', 'limit=3', 'order=price_usd.asc'];

      if (parsed.shape) {
        // Handle cushion variants
        var shape = parsed.shape;
        if (shape === 'Square Cushion' || shape === 'Long Cushion') {
          params.push('shape=in.(Cushion,CUSHION MODIFIED,CUSHION BRILLIANT)');
        } else {
          params.push('shape=eq.' + encodeURIComponent(shape));
        }
      }
      if (parsed.carat) {
        var c = parsed.carat;
        var isWhole = c === Math.floor(c);
        params.push('carat=gte.' + c.toFixed(2));
        params.push('carat=lte.' + (isWhole ? (c + 0.99).toFixed(2) : (c + 0.09).toFixed(2)));
      }
      if (parsed.color) {
        params.push('color=eq.' + parsed.color);
      }
      if (parsed.clarity) {
        params.push('clarity=eq.' + parsed.clarity);
      }
      if (parsed.cut) {
        params.push('cut=eq.' + parsed.cut);
      }
      if (parsed.lab) {
        params.push('lab=eq.' + parsed.lab);
      }

      var url = REST_BASE + '?' + params.join('&');

      return fetch(url, {
        headers: {
          'apikey': SUPABASE_ANON,
          'Authorization': 'Bearer ' + SUPABASE_ANON
        }
      })
      .then(function(res) { return res.ok ? res.json() : []; })
      .catch(function() { return []; });
    }

    // ── Inject diamond section into results ────────────────────
    function injectDiamondSection(container, parsed, diamonds) {
      // Remove previous
      var existing = container.querySelector('.results--diamonds');
      if (existing) existing.remove();
      var existingSep = container.querySelector('.diamond-sep');
      if (existingSep) existingSep.remove();

      var suggestions = window.DiamondQueryParser.buildSuggestions(parsed);
      if (suggestions.length === 0 && diamonds.length === 0) return;

      var html = '<div class="results results--diamonds">';

      // Suggestion links
      if (suggestions.length > 0) {
        html += '<div style="margin-bottom:' + (diamonds.length ? '12px' : '0') + ';">';
        for (var i = 0; i < suggestions.length; i++) {
          var s = suggestions[i];
          html += '<a href="' + esc(s.url) + '" style="';
          html += 'display:flex;align-items:center;gap:10px;padding:7px 4px;';
          html += 'text-decoration:none;color:#001f1a;font-size:14px;';
          html += 'border-bottom:1px solid #f0f0f0;transition:background 0.15s;border-radius:4px;"';
          html += ' onmouseover="this.style.background=\'#f8f2e6\'"';
          html += ' onmouseout="this.style.background=\'transparent\'">';
          html += '<span style="display:inline-flex;align-items:center;justify-content:center;';
          html += 'width:26px;height:26px;background:#f8f2e6;border-radius:4px;flex-shrink:0;">';
          html += '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c79135" stroke-width="2.5">';
          html += '<polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>';
          html += '</svg></span>';
          html += '<span style="font-weight:500;">' + esc(s.label) + '</span>';
          html += '<span style="margin-left:auto;color:#c79135;font-size:11px;font-weight:600;">View All →</span>';
          html += '</a>';
        }
        html += '</div>';
      }

      // Diamond inventory cards
      if (diamonds.length > 0) {
        html += '<p style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;font-weight:600;color:#999;margin:0 0 8px;padding:0 4px;">From Our Inventory</p>';
        html += '<div style="display:flex;gap:8px;overflow-x:auto;padding:0 2px 8px;">';

        for (var d = 0; d < diamonds.length; d++) {
          var dm = diamonds[d];
          var price = Number(dm.markup_price || dm.price_usd);
          var isRound = (dm.shape || '').toLowerCase() === 'round';
          var fourthVal = isRound ? (dm.cut || '—') : (dm.ratio ? Number(dm.ratio).toFixed(2) : '—');
          var fourthLabel = isRound ? 'Cut' : 'Ratio';

          html += '<a href="/pages/diamond-detail?sku=' + esc(dm.sku) + '" ';
          html += 'onclick="sessionStorage.setItem(\'dd_diamond\',' + esc(JSON.stringify(dm).replace(/'/g, "\\'" )) + ')" ';
          html += 'style="flex:0 0 150px;background:#fff;border:1px solid #eee;border-radius:6px;';
          html += 'text-decoration:none;color:#001f1a;overflow:hidden;transition:border-color 0.15s;"';
          html += ' onmouseover="this.style.borderColor=\'#c79135\'"';
          html += ' onmouseout="this.style.borderColor=\'#eee\'">';

          // Image
          if (dm.image_url) {
            html += '<div style="height:100px;background:#fafafa;display:flex;align-items:center;justify-content:center;overflow:hidden;">';
            html += '<img src="' + esc(dm.image_url) + '" alt="' + esc(dm.shape) + '" style="max-width:100%;max-height:100%;object-fit:contain;" loading="lazy">';
            html += '</div>';
          } else {
            html += '<div style="height:100px;background:#f8f2e6;display:flex;align-items:center;justify-content:center;font-size:11px;color:#999;">';
            html += esc(dm.shape) + ' ' + dm.carat + 'ct</div>';
          }

          // Info
          html += '<div style="padding:8px;">';
          html += '<div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:4px;">';
          html += '<span style="font-size:11px;font-weight:600;">' + esc(dm.shape) + '</span>';
          html += '<span style="font-size:12px;font-weight:700;color:#001f1a;">$' + price.toLocaleString() + '</span>';
          html += '</div>';

          // Stats row
          html += '<div style="display:flex;gap:2px;">';
          var stats = [
            { val: dm.carat, label: 'ct' },
            { val: dm.color, label: '' },
            { val: dm.clarity, label: '' },
            { val: fourthVal, label: '' }
          ];
          for (var si = 0; si < stats.length; si++) {
            html += '<span style="flex:1;text-align:center;font-size:10px;font-weight:600;';
            html += 'background:#f5f5f5;padding:3px 2px;border-radius:3px;">';
            html += esc(String(stats[si].val)) + stats[si].label + '</span>';
          }
          html += '</div>';
          html += '</div></a>';
        }
        html += '</div>';
      }

      html += '</div>';
      html += '<div class="diamond-sep" style="height:1px;background:#e5e5e5;margin:4px 0 6px;"></div>';

      // Prepend to results
      var target = container.querySelector('.predictive-search-results');
      if (target) {
        target.insertAdjacentHTML('afterbegin', html);
      } else if (container.children.length > 0) {
        container.insertAdjacentHTML('afterbegin', html);
      } else {
        container.innerHTML = html;
      }
    }

    // ── Intercept form submission for diamond queries ───────────
    var searchElements = document.querySelectorAll('predictive-search');
    for (var i = 0; i < searchElements.length; i++) {
      var form = searchElements[i].querySelector('form');
      if (!form) continue;

      form.addEventListener('submit', function(e) {
        var input = this.querySelector('input[type="search"], input[name="q"]');
        if (!input) return;

        var searchTerm = input.value.trim();
        if (!searchTerm) return;

        var parsed = window.DiamondQueryParser.parse(searchTerm);
        if (parsed.isDiamondQuery) {
          e.preventDefault();
          window.location.href = window.DiamondQueryParser.buildURL(parsed);
        }
      });
    }
  });

  // ─── Utilities ───────────────────────────────────────────────
  function esc(s) {
    if (!s) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(s));
    return div.innerHTML;
  }
})();